// src/pages/Login.jsx - FIXED VERSION with proper JWT token persistence
import React, { useState } from 'react';
import { EyeIcon, SparklesIcon } from 'lucide-react';
import { EyeSlashIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Footer from "../components/layout/footer.jsx";

function Login() {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    const { login, loading, verify2FA} = useAuth();
    const navigate = useNavigate();
    const [requires2FA, setRequires2FA] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [verifying2FA, setVerifying2FA] = useState(false);

    const handleChange = (e) => {
        const { name, type, checked, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.email || !formData.password) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            const result = await login(formData);

            if (result.success) {
                if (result.requires2FA) {
                    // 2FA required - show code input
                    setRequires2FA(true);
                    setUserEmail(formData.email);
                    toast.success(result.message || 'Verification code sent to your email');
                } else {
                    // Standard login success
                    toast.success(`Welcome back!${formData.rememberMe ? ' (Remembered for 30 days)' : ''}`);
                    navigate('/dashboard');
                }
            } else {
                console.error('❌ Login failed:', result.error);
                toast.error(result.error || 'Login failed');
            }
        } catch (error) {
            console.error('❌ Login error:', error);
            toast.error('Login failed. Please try again.');
        }
    };

    const handle2FASubmit = async (e) => {
        e.preventDefault();

        if (!twoFactorCode || twoFactorCode.length !== 6) {
            toast.error('Please enter a valid 6-digit code');
            return;
        }

        setVerifying2FA(true); // Start loading

        try {
            console.log('🔐 Sending 2FA verification request:', { email: userEmail, code: twoFactorCode });

            const response = await fetch('/api/auth/verify-2fa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userEmail,
                    code: twoFactorCode
                }),
            });

            console.log('📡 2FA Response status:', response.status);

            const data = await response.json();
            console.log('📡 2FA Response data:', data);

            if (data.success) {
                // Use the same token storage logic as AuthContext
                const storage = formData.rememberMe ? localStorage : sessionStorage;
                storage.setItem('token', data.token);

                if (formData.rememberMe) {
                    localStorage.setItem('rememberMe', 'true');
                    localStorage.setItem('loginTime', new Date().toISOString());
                    localStorage.setItem('userInfo', JSON.stringify(data.user));
                } else {
                    sessionStorage.setItem('loginTime', new Date().toISOString());
                }

                toast.success('Login successful!');

                // Force a page reload to trigger AuthContext initialization
                window.location.href = '/dashboard';

            } else {
                setVerifying2FA(false); // Stop loading on error
                toast.error(data.message || '2FA verification failed');
            }
        } catch (error) {
            console.error('❌ 2FA verification error:', error);
            setVerifying2FA(false); // Stop loading on error
            toast.error('Verification failed. Please try again.');
        }
    };

    const handleResendCode = async () => {
        try {
            const response = await fetch('/api/auth/enable-2fa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                },
            });

            if (response.ok) {
                toast.success('New verification code sent!');
                setTwoFactorCode(''); // Clear current code
            } else {
                // Fallback - trigger another login attempt to regenerate code
                const result = await login({ email: userEmail, password: formData.password });
                if (result.requires2FA) {
                    toast.success('New verification code sent!');
                    setTwoFactorCode('');
                }
            }
        } catch (error) {
            toast.error('Failed to resend code. Please try logging in again.');
        }
    };

    const getStorageDebugInfo = () => {
        return {
            localStorage: {
                token: localStorage.getItem('token') ? 'Present' : 'None',
                tokenLength: localStorage.getItem('token')?.length || 0,
                rememberMe: localStorage.getItem('rememberMe') || 'None',
                loginTime: localStorage.getItem('loginTime') || 'None',
                userInfo: localStorage.getItem('userInfo') ? 'Present' : 'None',
                allKeys: Object.keys(localStorage)
            },
            sessionStorage: {
                token: sessionStorage.getItem('token') ? 'Present' : 'None',
                tokenLength: sessionStorage.getItem('token')?.length || 0,
                loginTime: sessionStorage.getItem('loginTime') || 'None',
                allKeys: Object.keys(sessionStorage)
            },
            cookieCount: typeof document !== 'undefined' ? document.cookie.split(';').length : 0
        };
    };

    const clearAllStorage = () => {
        localStorage.clear();
        sessionStorage.clear();
        toast.success('All browser storage cleared!');
    };

    const testStoragePersistence = () => {
        const testKey = 'persistence_test';
        const testValue = Date.now().toString();

        if (formData.rememberMe) {
            localStorage.setItem(testKey, testValue);
            setTimeout(() => {
                const retrieved = localStorage.getItem(testKey);
                if (retrieved === testValue) {
                    toast.success('✅ localStorage persistence working');
                } else {
                    toast.error('❌ localStorage persistence failed');
                }
                localStorage.removeItem(testKey);
            }, 100);
        } else {
            sessionStorage.setItem(testKey, testValue);
            setTimeout(() => {
                const retrieved = sessionStorage.getItem(testKey);
                if (retrieved === testValue) {
                    toast.success('✅ sessionStorage persistence working');
                } else {
                    toast.error('❌ sessionStorage persistence failed');
                }
                sessionStorage.removeItem(testKey);
            }, 100);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navigation */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center">
                                <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                <span className="ml-2 text-2xl font-bold text-blue-600">PostMgr</span>
                            </Link>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="../#features" className="text-gray-700 hover:text-blue-600">Features</a>
                            <a href="../#pricing" className="text-gray-700 hover:text-blue-600">Pricing</a>
                            <Link to="/about" className="text-gray-700 hover:text-blue-600">About</Link>
                            <Link to="/contact" className="text-gray-700 hover:text-blue-600">Contact</Link>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Link to="/login" className="text-gray-700 hover:text-blue-600">
                                Sign In
                            </Link>
                            <Link
                                to="/register"
                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <div className="flex justify-center">
                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                    </div>
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        Sign in to PostMgr
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Or{' '}
                        <Link
                            to="/register"
                            className="font-medium text-blue-600 hover:text-blue-500"
                        >
                            create a new account
                        </Link>
                    </p>
                </div>

                {/* Login Form */}
                {/* Conditional Form Rendering */}
                {!requires2FA ? (
                    /* Original Login Form */
                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="space-y-4">
                            {/* Email Field */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email address
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter your email"
                                />
                            </div>

                            {/* Password Field */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <div className="mt-1 relative">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? (
                                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Remember Me and Forgot Password */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="rememberMe"
                                    name="rememberMe"
                                    type="checkbox"
                                    checked={formData.rememberMe}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                                    Remember me
                                </label>
                                <div className="ml-2 group relative">
                                    <span className="text-blue-500 cursor-help text-sm">ℹ️</span>
                                    <div className="opacity-0 group-hover:opacity-100 absolute bottom-6 left-0 bg-black text-white text-xs rounded py-2 px-3 z-10 w-64 transition-opacity">
                                        {formData.rememberMe
                                            ? "✅ Token stored in localStorage - stays logged in for 30 days (persists across browser restarts)"
                                            : "⚠️ Token stored in sessionStorage - logged out when browser closes"
                                        }
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/forgot-password"
                                className="text-sm font-medium text-blue-600 hover:text-blue-500"
                            >
                                Forgot your password?
                            </Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                                loading
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                            } transition-colors`}
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Signing in...
                                </div>
                            ) : (
                                'Sign in'
                            )}
                        </button>

                        {/* Debug Toggle */}
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setDebugMode(!debugMode)}
                                className="text-xs text-gray-500 hover:text-gray-700"
                            >
                                {debugMode ? 'Hide' : 'Show'} Token Debug Info
                            </button>
                        </div>

                        {/* Debug Panel */}
                        {debugMode && (
                            <div className="bg-gray-100 border border-gray-300 rounded-md p-4 mt-4">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">🔍 JWT Token Persistence Debug</h4>
                                    <div className="space-x-2">
                                        <button
                                            type="button"
                                            onClick={clearAllStorage}
                                            className="text-xs bg-red-500 text-white px-2 py-1 rounded"
                                        >
                                            Clear All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={testStoragePersistence}
                                            className="text-xs bg-green-500 text-white px-2 py-1 rounded"
                                        >
                                            Test Storage
                                        </button>
                                    </div>
                                </div>

                                <div className="text-xs space-y-3">
                                    <div>
                                        <strong>Current Form State:</strong>
                                        <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify({
                                email: formData.email,
                                hasPassword: !!formData.password,
                                rememberMe: formData.rememberMe,
                                storageMode: formData.rememberMe ? 'localStorage (30 days)' : 'sessionStorage (until browser close)'
                            }, null, 2)}
                        </pre>
                                    </div>

                                    <div>
                                        <strong>Token Storage Status:</strong>
                                        <pre className="bg-white p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(getStorageDebugInfo(), null, 2)}
                        </pre>
                                    </div>

                                    <div>
                                        <strong>JWT Token Persistence Protocol:</strong>
                                        <div className="bg-white p-3 rounded mt-1 text-gray-700">
                                            <div className="space-y-2">
                                                <p><strong>Remember Me = true:</strong></p>
                                                <ul className="ml-4 space-y-1 text-xs">
                                                    <li>• Token stored in localStorage</li>
                                                    <li>• Persists across browser restarts</li>
                                                    <li>• Expires after 30 days (server-side JWT expiry)</li>
                                                    <li>• User info cached for faster app loading</li>
                                                    <li>• Login time tracked for analytics</li>
                                                </ul>

                                                <p><strong>Remember Me = false:</strong></p>
                                                <ul className="ml-4 space-y-1 text-xs">
                                                    <li>• Token stored in sessionStorage</li>
                                                    <li>• Cleared when browser/tab closes</li>
                                                    <li>• More secure for shared computers</li>
                                                    <li>• No user info cached</li>
                                                </ul>

                                                <p><strong>Token Validation:</strong></p>
                                                <ul className="ml-4 space-y-1 text-xs">
                                                    <li>• Checked on app startup (AuthContext)</li>
                                                    <li>• Validated with server (/api/auth/validate)</li>
                                                    <li>• Automatic logout if token invalid/expired</li>
                                                    <li>• Graceful fallback to login page</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                ) : (
                    /* 2FA Verification Form */
                    <form onSubmit={handle2FASubmit} className="mt-8 space-y-6">
                        <div className="text-center">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <div className="flex items-center justify-center mb-2">
                                    <div className="bg-blue-100 rounded-full p-2">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-lg font-medium text-blue-900 mb-1">Two-Factor Authentication</h3>
                                <p className="text-sm text-blue-700">
                                    We've sent a 6-digit verification code to <strong>{userEmail}</strong>
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700">
                                    Verification Code
                                </label>
                                <input
                                    id="twoFactorCode"
                                    name="twoFactorCode"
                                    type="text"
                                    required
                                    maxLength={6}
                                    value={twoFactorCode}
                                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono tracking-widest"
                                    placeholder="000000"
                                    autoComplete="one-time-code"
                                />
                                <p className="mt-1 text-xs text-gray-500 text-center">
                                    Enter the 6-digit code from your email
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col space-y-3">
                            <button
                                type="submit"
                                disabled={verifying2FA || twoFactorCode.length !== 6}
                                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                                    verifying2FA || twoFactorCode.length !== 6
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                } transition-colors`}
                            >
                                {verifying2FA ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Verifying...
                                    </div>
                                ) : (
                                    'Verify Code'
                                )}
                            </button>

                            <div className="flex justify-between items-center text-sm">
                                <button
                                    type="button"
                                    onClick={() => setRequires2FA(false)}
                                    className="text-gray-600 hover:text-gray-800"
                                >
                                    ← Back to login
                                </button>

                                <button
                                    type="button"
                                    onClick={handleResendCode}
                                    className="text-blue-600 hover:text-blue-500"
                                >
                                    Resend code
                                </button>
                            </div>
                        </div>

                        <div className="text-center">
                            <div className="text-xs text-gray-500 space-y-1 bg-yellow-50 p-3 rounded-md">
                                <p className="font-medium text-yellow-800">📧 Code expires in 10 minutes</p>
                                <p>Check your spam folder if you don't see the email</p>
                            </div>
                        </div>
                    </form>
                )}

                {/* Security Info */}
                <div className="text-center">
                    <div className="text-xs text-gray-500 space-y-1 bg-blue-50 p-3 rounded-md">
                        <p className="font-medium text-blue-800">🔐 Security Features:</p>
                        <p><strong>JWT Tokens:</strong> Signed with HS256, 30-day expiration</p>
                        <p><strong>Storage:</strong> {formData.rememberMe ? 'Persistent (localStorage)' : 'Session-only (sessionStorage)'}</p>
                        <p><strong>Validation:</strong> Server-side verification on each request</p>
                        <p><strong>Auto-logout:</strong> Invalid/expired tokens cleared automatically</p>
                    </div>
                </div>
            </div>
        </div>
            <Footer/>
        </div>
    );
}

export default Login;